import { useEffect, useState } from "react";

/**
 * Tiny QR thumbnail for the codes list and detail headers. Pulls
 * `/v1/codes/{id}/image?format=png` through the same-origin BFF proxy —
 * the session cookie travels with the request, no auth header needed.
 *
 * Mirrors web/src/components/app/code-thumbnail.tsx so the visual
 * contract is identical to the legacy Next surface: square box, fixed
 * size in px, `image-rendering: pixelated` so the QR modules stay crisp
 * when the rendered PNG happens to be larger than the displayed size.
 *
 * Why not @qrody/qr-renderer here: the list endpoint returns a generic
 * payload `Record<string, unknown>`. Translating that back into the
 * typed AnonymousPayload / AnonymousDesign the client renderer wants is
 * the editor's job (sub-iter #1+). For a list thumbnail the rendered
 * server image is faster to wire and matches the legacy semantics.
 *
 * `cacheKey` lets the caller force a refetch after the underlying code
 * mutates — the GET endpoint returns the same URL even if payload/design
 * have changed, so without an explicit key the browser keeps the stale
 * thumbnail. `JSON.stringify(payload + design)` works well enough.
 *
 * Visible failure: any fetch error, non-OK status, or `<img>` decode
 * error renders a labelled "n/a" placeholder instead of the same pulsing
 * skeleton that means "still loading". Without this distinction a server
 * 401 looks like an indefinite spinner.
 */
type Phase = "loading" | "ready" | "failed";

export function CodeThumbnail({
  id,
  size = 48,
  cacheKey = "",
}: {
  id: string;
  size?: number;
  cacheKey?: string;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("loading");

  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    setPhase("loading");
    setSrc(null);
    void (async () => {
      try {
        const response = await fetch(
          `/v1/codes/${encodeURIComponent(id)}/image?format=png`,
          { credentials: "include" },
        );
        if (cancelled) return;
        if (!response.ok) {
          console.warn(
            `[CodeThumbnail] /v1/codes/${id}/image returned ${response.status} ${response.statusText}`,
          );
          setPhase("failed");
          return;
        }
        const blob = await response.blob();
        if (cancelled) return;
        if (blob.size === 0) {
          console.warn(`[CodeThumbnail] /v1/codes/${id}/image returned an empty body`);
          setPhase("failed");
          return;
        }
        url = URL.createObjectURL(blob);
        setSrc(url);
        setPhase("ready");
      } catch (ex) {
        if (!cancelled) {
          console.warn(`[CodeThumbnail] /v1/codes/${id}/image failed`, ex);
          setPhase("failed");
        }
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [id, cacheKey]);

  // Hard square box independent of the parent layout (flex/grid/table).
  // `aspect-ratio: 1/1` + explicit width *and* min-/max-width keep the QR
  // exactly `size × size` on every viewport, including narrow mobile
  // widths where the surrounding container starts to scroll.
  const boxStyle = {
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
    maxWidth: size,
    maxHeight: size,
    aspectRatio: "1 / 1",
  } as const;

  if (phase === "loading") {
    return (
      <span
        aria-hidden
        className="inline-block shrink-0 animate-pulse rounded-sm bg-muted"
        style={boxStyle}
      />
    );
  }

  if (phase === "failed" || !src) {
    return (
      <span
        role="img"
        aria-label="QR-Vorschau nicht verfügbar"
        className="inline-flex shrink-0 items-center justify-center rounded-sm border border-dashed border-border bg-muted/40 text-[10px] font-medium text-muted-foreground"
        style={boxStyle}
        title="QR-Vorschau nicht verfügbar"
      >
        n/a
      </span>
    );
  }

  return (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      className="block shrink-0 rounded-sm border border-border bg-white object-contain"
      // `pixelated` keeps the QR modules sharp instead of letting the
      // browser bilerp when the rendered PNG is larger than `size`.
      style={{ ...boxStyle, imageRendering: "pixelated" }}
      onError={() => {
        console.warn(`[CodeThumbnail] <img> decode failed for code ${id}`);
        setPhase("failed");
      }}
    />
  );
}
